import '@theme/br-size-chart-viewer';
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Returns the canonical product URL used by customer-facing detail links.
 * Quick view may fetch a dedicated `view` template, but that parameter must
 * never leak into links that navigate to the full product page.
 * @param {string} productUrl
 * @returns {string}
 */
function getProductDetailsUrl(productUrl) {
  if (!productUrl) return '';

  const url = new URL(productUrl, window.location.origin);
  url.searchParams.delete('view');
  return url.href;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    const productUrl = productLink?.href || this.dataset.productUrl || '';
    return this.dataset.modalContext === 'print' ? getProductDetailsUrl(productUrl) : productUrl;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    // Progressive enhancement: some triggers are crawlable links (e.g. print
    // selector swatches). Let modified and non-primary clicks follow the href
    // instead of opening Quick View. Buttons have no default navigation.
    const trigger =
      event.target instanceof Element
        ? /** @type {HTMLButtonElement | HTMLAnchorElement | null} */ (event.target.closest('button, a'))
        : null;
    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    const isNonPrimaryMouseClick = event instanceof MouseEvent && event.button !== 0;

    if (
      trigger instanceof HTMLAnchorElement &&
      (isModifiedClick || isNonPrimaryMouseClick)
    ) {
      return;
    }

    event.preventDefault();

    if (trigger?.getAttribute('aria-busy') === 'true') return;

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.#setTriggerLoading(trigger, true);

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (!html) return;

        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }

      if (!productGrid) throw new Error('Quick view content was not available.');

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('[quick-add] Unable to open quick view:', error);

      if (trigger instanceof HTMLAnchorElement && trigger.href) {
        window.location.assign(trigger.href);
      }
    } finally {
      this.#setTriggerLoading(trigger, false);
    }
  };

  /**
   * Shows immediate feedback while the requested product content is loading.
   * @param {HTMLButtonElement | HTMLAnchorElement | null} trigger
   * @param {boolean} loading
   */
  #setTriggerLoading(trigger, loading) {
    if (!trigger) return;

    trigger.classList.toggle(
      'is-loading',
      loading
    );

    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = loading;
    } else if (trigger instanceof HTMLAnchorElement) {
      if (loading) {
        trigger.setAttribute('aria-disabled', 'true');
      } else {
        trigger.removeAttribute('aria-disabled');
      }
    }

    trigger.setAttribute(
      'aria-busy',
      String(loading)
    );
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const dialog = dialogComponent.refs?.dialog;
    if (dialog) {
      const modalContext = this.dataset.modalContext;
      if (modalContext) {
        // Print uses the same presentation as the approved collection quick view.
        dialog.dataset.source = modalContext === 'print' ? 'collection' : modalContext;
      } else {
        delete dialog.dataset.source;
      }
    }

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // PDP galleries repeat the award badge per image. Quick Add uses one overlay
    // and its own viewer because only the product grid is copied into the modal.
    const awardMedia = productGrid.querySelector('.product-information__media');
    const awardBadges = [...productGrid.querySelectorAll('.br-zippies-award-badge')];
    const awardBadge = awardBadges[0];
    awardBadges.forEach((badge) => badge.remove());
    if (awardMedia && awardBadge) {
      awardBadge.classList.remove('br-zippies-award-badge--pdp', 'br-zippies-award-badge--desktop');
      awardBadge.classList.add('br-zippies-award-badge--quick-add');
      awardBadge.removeAttribute('on:click');
      awardBadge.setAttribute('data-br-award-open', 'BrAwardViewer-quick-add');
      awardBadge.setAttribute('aria-controls', 'BrAwardViewer-quick-add');
      awardMedia.appendChild(awardBadge);
    }

    const modalContext =
      this.dataset.modalContext || '';

    const isCompactPurchaseQuickAdd =
      modalContext === 'collection' ||
      modalContext === 'print' ||
      modalContext === 'cart';

    if (isCompactPurchaseQuickAdd) {
      const productDetails =
        productGrid.querySelector('.product-details');

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector('variant-picker');

      const productPrice =
        productGrid.querySelector('product-price');

      const restockNotify =
        productGrid.querySelector(
          'br-restock-notify'
        );

      /*
      * Collection Quick Add uses one compact structure
      * on both desktop and mobile instead of keeping
      * the full PDP details column on desktop.
      */
      const productHeader =
        document.createElement('div');

      productHeader.className =
        'product-header br-collection-quick-view__header';

      const productTitle =
        document.createElement('a');

      productTitle.className =
        'br-collection-quick-view__title';

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      productHeader.appendChild(productTitle);

      if (productPrice) {
        productHeader.appendChild(productPrice);
      }

      const controls =
        document.createElement('div');

      controls.className =
        'br-collection-quick-view__controls';

      if (variantPicker) {
        controls.appendChild(variantPicker);
      }

      if (restockNotify) {
        controls.appendChild(restockNotify);
      }

      if (productFormComponent) {
        controls.appendChild(
          productFormComponent
        );
      }

      productGrid.append(
        productHeader,
        controls
      );

      /*
      * Product title, price, Variant Picker,
      * Restock and Product Form have already
      * been preserved above.
      *
      * Everything else from PDP is deliberately
      * removed from Collection Quick Add.
      */
      productDetails?.remove();

    } else if (
      isMobileBreakpoint() &&
      this.dataset.modalContext !== 'print'
    ) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

          productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    } 

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    if (this.dataset.modalContext === 'print' && !isCompactPurchaseQuickAdd) {
      this.#preparePrintQuickView(
        modalContent
      );
    }

    if (isCompactPurchaseQuickAdd) {
      this.#prepareCollectionQuickView(
        modalContent
      );
    }
  }

  /**
   * Adds the lightweight product-details link used by the Print quick view.
   * The purchase controls remain the native Shopify product form.
   * @param {Element} modalContent
   */
  #preparePrintQuickView(modalContent) {
    const productDetails = modalContent.querySelector('.product-details');
    const productTitleLink = /** @type {HTMLAnchorElement | null} */ (
      modalContent.querySelector('.view-product-title a')
    );
    if (!productDetails || !productTitleLink) return;

    const detailsLink = document.createElement('a');
    detailsLink.className = 'br-quick-view__details-link';
    detailsLink.href = getProductDetailsUrl(productTitleLink.href);
    detailsLink.textContent = this.dataset.detailsLabel || 'View product details';
    productDetails.appendChild(detailsLink);

    this.#enablePrintGallery(modalContent);
  }

  /**
   * Finishes the compact Collection Quick Add layout.
   *
   * Adds:
   * - View product details
   * - mobile swipe gallery + image counter
   *
   * Desktop keeps Horizon's native vertical media gallery.
   *
   * Existing Horizon Product Form, Variant Picker,
   * Restock and Wishlist remain untouched.
   *
   * @param {Element} modalContent
   */
  #prepareCollectionQuickView(modalContent) {
    const controls =
      modalContent.querySelector(
        '.br-collection-quick-view__controls'
      );

    if (
      controls &&
      !controls.querySelector(
        '.br-quick-view__details-link'
      )
    ) {
      const detailsLink =
        document.createElement('a');

      detailsLink.className =
        'br-quick-view__details-link';

      detailsLink.href =
        this.productPageUrl;

      detailsLink.textContent =
        this.dataset.detailsLabel ||
        'View product details';

      controls.appendChild(
        detailsLink
      );
    }

    /*
     * Collection Quick Add gallery enhancement
     * is intentionally mobile-only.
     *
     * Desktop keeps Horizon's native vertical
     * media gallery without rebuilding thumbnails.
     */
    if (
      ['collection', 'print'].includes(this.dataset.modalContext || '') &&
      isMobileBreakpoint()
    ) {
      this.#enableCollectionGallery(
        modalContent
      );
    }
  }


  /**
   * Converts Horizon's existing product media slideshow
   * into the compact mobile Collection Quick Add gallery.
   *
   * Mobile:
   * - native horizontal swipe
   * - active image counter
   *
   * No custom thumbnails are created.
   *
   * Desktop:
   * - this method is not called
   * - Horizon's native vertical gallery remains intact
   *
   * @param {Element} modalContent
   */
  #enableCollectionGallery(modalContent) {
    const gallery =
      modalContent.querySelector(
        '.product-information__media media-gallery'
      );

    const scroller =
      gallery?.querySelector(
        'slideshow-slides'
      );

    const slides = gallery
      ? [
          ...gallery.querySelectorAll(
            'slideshow-slide'
          ),
        ]
      : [];

    if (
      !gallery ||
      !scroller ||
      slides.length === 0
    ) {
      return;
    }

    /*
     * Remove navigation left from a previously
     * rendered product before rebuilding it.
     */
    gallery
      .querySelector(
        '.br-collection-quick-view__gallery-nav'
      )
      ?.remove();

    /*
     * A single-image product does not need
     * navigation or a counter.
     */
    gallery.toggleAttribute('data-br-gallery-two-up', slides.length > 1);

    if (slides.length === 1) {
      slides[0].setAttribute(
        'aria-hidden',
        'false'
      );

      return;
    }

    const navigation =
      document.createElement('div');

    navigation.className =
      'br-collection-quick-view__gallery-nav';

    const counter =
      document.createElement('span');

    counter.className =
      'br-collection-quick-view__counter';

    counter.setAttribute(
      'aria-live',
      'polite'
    );

    let activeIndex =
      Math.max(
        0,
        slides.findIndex(
          (slide) =>
            slide.getAttribute(
              'aria-hidden'
            ) === 'false'
        )
      );

    const updateState = (
      nextIndex
    ) => {
      activeIndex =
        Math.max(
          0,
          Math.min(
            slides.length - 2,
            nextIndex
          )
        );

      slides.forEach(
        (slide, index) => {
          slide.setAttribute(
            'aria-hidden',
            index === activeIndex || index === activeIndex + 1
              ? 'false'
              : 'true'
          );
        }
      );

      counter.textContent =
        `${activeIndex + 1}–${Math.min(activeIndex + 2, slides.length)} / ${slides.length}`;
    };

    navigation.appendChild(
      counter
    );

    gallery.appendChild(
      navigation
    );

    let scrollFrame = 0;

    scroller.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(
          scrollFrame
        );

        scrollFrame =
          requestAnimationFrame(
            () => {
              const step = slides[0].getBoundingClientRect().width + 8;
              if (step <= 0) return;
              updateState(Math.round(scroller.scrollLeft / step));
            }
          );
      },
      {
        passive: true,
      }
    );

    updateState(
      activeIndex
    );
  }
  /**
   * Adapts Horizon's grid-gallery markup into a compact quick-view carousel.
   * Existing media, alt text, and arrow icons are reused.
   * @param {Element} modalContent
   */
  #enablePrintGallery(modalContent) {
    const scroller = /** @type {HTMLElement | null} */ (modalContent.querySelector('slideshow-slides'));
    const slides = [...modalContent.querySelectorAll('slideshow-slide')];
    const arrows = modalContent.querySelector('slideshow-arrows');
    const previousButton = /** @type {HTMLButtonElement | null} */ (
      arrows?.querySelector('.slideshow-control--previous')
    );
    const nextButton = /** @type {HTMLButtonElement | null} */ (arrows?.querySelector('.slideshow-control--next'));
    const gallery = modalContent.querySelector('media-gallery');
    const nativeThumbnailImages = [
      ...modalContent.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail img'),
    ];
    if (!scroller || !gallery || slides.length === 0) return;

    gallery.querySelector('.br-quick-view-gallery__navigation')?.remove();

    if (slides.length < 2 || !previousButton || !nextButton) {
      gallery.classList.add('br-quick-view-gallery--single');
      return;
    }

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false')
    );

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const navigation = document.createElement('div');
    const thumbnails = document.createElement('div');
    const counter = document.createElement('span');
    const thumbnailButtons = [];

    gallery.classList.remove('br-quick-view-gallery--single');
    navigation.className = 'br-quick-view-gallery__navigation';
    navigation.setAttribute('aria-label', 'Product images');
    thumbnails.className = 'br-quick-view-gallery__thumbnails';
    counter.className = 'br-quick-view-gallery__counter';
    counter.setAttribute('aria-live', 'polite');

    const updateState = (nextIndex) => {
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => slide.setAttribute('aria-hidden', index === activeIndex ? 'false' : 'true'));
      thumbnailButtons.forEach((button, index) => {
        button.setAttribute('aria-selected', String(index === activeIndex));
      });
      counter.textContent = `${activeIndex + 1} / ${slides.length}`;
    };

    const showSlide = (nextIndex) => {
      updateState(nextIndex);
      scroller.scrollTo({
        left: activeIndex * scroller.clientWidth,
        behavior: reducedMotion ? 'instant' : 'smooth',
      });
    };

    const replaceArrow = (button, direction) => {
      const replacement = /** @type {HTMLButtonElement} */ (button.cloneNode(true));
      replacement.removeAttribute('on:click');
      replacement.addEventListener('click', () => showSlide(activeIndex + direction));
      button.replaceWith(replacement);
    };

    replaceArrow(previousButton, -1);
    replaceArrow(nextButton, 1);

    slides.forEach((slide, index) => {
      const button = document.createElement('button');
      const sourceImage = /** @type {HTMLImageElement | null} */ (
        nativeThumbnailImages[index] || slide.querySelector('img')
      );

      button.type = 'button';
      button.className = 'br-quick-view-gallery__thumbnail';
      button.setAttribute('aria-label', `View image ${index + 1} of ${slides.length}`);
      button.addEventListener('click', () => showSlide(index));

      if (sourceImage) {
        const image = /** @type {HTMLImageElement} */ (sourceImage.cloneNode(false));
        image.alt = '';
        image.loading = 'lazy';
        image.removeAttribute('sizes');
        image.removeAttribute('srcset');
        image.src = sourceImage.currentSrc || sourceImage.src;
        button.appendChild(image);
      }

      thumbnailButtons.push(button);
      thumbnails.appendChild(button);
    });

    navigation.append(thumbnails, counter);
    gallery.appendChild(navigation);

    let scrollFrame;
    scroller.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const width = scroller.clientWidth;
          if (width > 0) updateState(Math.round(scroller.scrollLeft / width));
        });
      },
      { passive: true }
    );

    updateState(activeIndex);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));
        const printDetailsLink = /** @type {HTMLAnchorElement} */ (
          this.querySelector('.br-quick-view__details-link')
        );

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
        if (printDetailsLink) printDetailsLink.href = getProductDetailsUrl(anchorElement.href);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
